import { defineStore, storeToRefs } from 'pinia'
import { nextTick } from 'vue'
import type { Ref } from 'vue'
import POSCheckReceiptDialog from '../components/POS/CheckReceiptDialog.vue'
import NewDineInCheckDialog from '../components/POS/NewDineInCheckDialog.vue'
import type { CheckCartItem } from '../types/checkCart'
import { calculateCheckCartItemTotal } from '../utils/checkCart.calculations'
import type { Check } from '~/types/checks'
import type { SyncCheckPayload } from '~/server/services/checksService'
import type { CartItem } from '~/types/commerce'
import { useChecksSyncStore } from '~/stores/shared/checksSync'
import { useChecksStore } from '~/stores/admin/checks'
import { useTablesStore } from '~/stores/admin/tables'
import { useCommerceStore } from '~/stores/pos/commerce'
import { useComboEditorStore } from '~/stores/combo/comboEditor'

/**
 * POS Check Store - Unified store for active check and cart management
 * Combines functionality from activeCheckStore and checkCartStore
 * Manages the currently active check, its cart, and all POS operations
 */
export const usePosCheckStore = defineStore('posCheck', () => {
  const log = useLogger('store:posCheck', true)
  const checksSyncStore = useChecksSyncStore()
  const { checks: syncedChecks } = storeToRefs(checksSyncStore)
  const checksStore = useChecksStore()
  const tablesStore = useTablesStore()
  const commerceStore = useCommerceStore()
  const comboEditor = useComboEditorStore()
  const { get, put, post } = useAuthenticatedFetch()
  const businessStore = useBusinessStore()
  const { businessId } = storeToRefs(businessStore)
  const shiftStore = useShiftStore()
  const { shiftStatus, shiftId } = storeToRefs(shiftStore)

  const nuxtApp = useNuxtApp()
  const { t } = nuxtApp.$i18n
  const { $dialog } = nuxtApp

  // Access commerce store data
  const { products, categories, combos } = storeToRefs(commerceStore)

  // Check state (from activeCheckStore)
  const selectedTableId: Ref<number | null> = ref(null)
  const activeCheckId: Ref<number | null> = ref(null)
  const checkData: Ref<Check | null> = ref(null)
  const isEditingClosedCheck: Ref<boolean> = ref(false)
  const originalCheckSnapshot: Ref<Check | null> = ref(null)

  // Cart state (from checkCartStore)
  const checkCart: Ref<CheckCartItem[]> = ref([])

  // UI preferences
  const compactViewEnabled: Ref<boolean> = ref(true)
  // TODO: Make configurable per business
  // const autoSendToKitchen: Ref<boolean> = ref(true)

  // Payment calculation state (for CloseCheckDialog)
  const selectedPaymentMethodId: Ref<number | null> = ref(null)
  const selectedDiscountId: Ref<number | null> = ref(null)
  const tipAmount: Ref<number> = ref(0)

  // Staging state for editing
  const productStaged = ref<any>(null)
  const productModal = ref<boolean>(false)

  // Combo editing state - exposed from shared comboEditor store
  const comboStaged = computed(() => comboEditor.comboStaged)
  const comboModal = computed({
    get: () => comboEditor.comboModal,
    set: (value: boolean) => { comboEditor.comboModal = value },
  })
  const comboCustomized = computed(() => comboEditor.comboCustomized)
  const comboCustomOptions = computed(() => comboEditor.comboCustomOptions)

  // Computed properties - Check
  const hasActiveCheck = computed(() => activeCheckId.value !== null)
  const selectedTable = computed(() => {
    if (!selectedTableId.value)
      return null
    return tablesStore.getTableById(selectedTableId.value)
  })

  // Computed properties - Cart
  const itemsInCart = computed((): number => {
    if (!Array.isArray(checkCart.value) || checkCart.value.length === 0) {
      return 0
    }
    return checkCart.value
      .filter(item => item.kitchen_status !== 'voided' && item.kitchen_status !== 'canceled')
      .reduce((total, item) => total + item.quantity, 0)
  })

  const checkCartTotal = computed((): string => {
    if (!Array.isArray(checkCart.value) || checkCart.value.length === 0) {
      return '0.00'
    }

    const total = checkCart.value
      .filter(item => item.kitchen_status !== 'voided' && item.kitchen_status !== 'canceled')
      .reduce((accumulator, item) => {
        if (item.total && typeof item.total === 'number' && item.type !== 'CUSTOM_COMBO') {
          return accumulator + item.total
        }
        return accumulator + calculateCheckCartItemTotal(item)
      }, 0)

    return Math.round(total).toString()
  })

  const hasItems = computed(() => checkCart.value.length > 0)

  const productStagedTotal = computed((): number => {
    if (!productStaged.value)
      return 0
    return productStaged.value.total || 0
  })

  const comboStagedTotal = computed(() => comboEditor.comboStagedTotal)

  // Payment calculations (for CloseCheckDialog)
  const subtotal = computed(() => Number(checkCartTotal.value))

  const selectedPaymentMethod = computed(() => {
    const paymentMethodsStore = usePaymentMethodsStore()
    if (!selectedPaymentMethodId.value) return null
    return paymentMethodsStore.paymentMethods.find(pm => pm.id === selectedPaymentMethodId.value) || null
  })

  const selectedDiscount = computed(() => {
    const discountsStore = useDiscountsStore()
    if (!selectedDiscountId.value) return null
    return discountsStore.discounts.find(d => d.id === selectedDiscountId.value) || null
  })

  const discountAmount = computed(() => {
    if (!selectedDiscount.value) return 0
    return subtotal.value * (selectedDiscount.value.percentage / 100)
  })

  const subtotalAfterDiscount = computed(() => {
    return subtotal.value - discountAmount.value
  })

  const paymentSurcharge = computed(() => {
    if (!selectedPaymentMethod.value) return 0
    return subtotalAfterDiscount.value * (selectedPaymentMethod.value.surcharge_percentage / 100)
  })

  const finalTotal = computed(() => {
    return subtotalAfterDiscount.value + tipAmount.value + paymentSurcharge.value
  })

  const canConfirmPayment = computed(() => {
    return selectedPaymentMethodId.value !== null
  })

  // Payment state setters
  function setSelectedPaymentMethod(id: number | null): void {
    selectedPaymentMethodId.value = id
  }

  function setSelectedDiscount(id: number | null): void {
    selectedDiscountId.value = id
  }

  function setTipAmount(amount: number): void {
    tipAmount.value = amount
  }

  function resetPaymentState(): void {
    selectedPaymentMethodId.value = null
    selectedDiscountId.value = null
    tipAmount.value = 0
  }

  // Check management methods
  async function openCheckForTable(tableId: number, customerData?: { customer_id?: number, customer_name?: string, customer_phone?: string }): Promise<boolean> {
    try {
      const table = tablesStore.getTableById(tableId)
      if (!table) {
        useErrorToast('Table not found')
        return false
      }

      // Check if table already has an open check
      await checksSyncStore.fetchChecks()
      const existingCheck = syncedChecks.value.find(
        (check: Check) => check.table_id === tableId && check.status === 'open',
      )

      if (existingCheck) {
        useErrorToast('Table {tableNumber} already has an open check', { tableNumber: table.table_number })
        return false
      }

      // Prevent same customer from having two open checks
      if (customerData?.customer_id) {
        const existingCustomerCheck = syncedChecks.value.find(
          (check: Check) => check.customer_id === customerData.customer_id && check.status === 'open',
        )
        if (existingCustomerCheck) {
          useErrorToast('{name} already has an open check', { name: customerData.customer_name || '' })
          return false
        }
      }

      // Create new check
      const checkForm = {
        check_type: 'dine_in' as const,
        table_id: tableId,
        shift_id: shiftId.value,
        guest_count: table.capacity || 1,
        notes: `Check for table ${table.table_number}`,
        ...customerData,
      }

      const success = await checksStore.createCheck(checkForm)
      if (!success) {
        return false
      }

      // Get the newly created check
      await checksSyncStore.fetchChecks()
      const newCheck = syncedChecks.value.find(
        (check: Check) => check.table_id === tableId && check.status === 'open',
      )

      if (newCheck) {
        // Clear any previous check state
        clearActiveCheck()

        activeCheckId.value = newCheck.id
        checkData.value = newCheck
        selectedTableId.value = tableId

        // Update table status to occupied
        await tablesStore.updateTableStatus(tableId, 'occupied')

        log.info(`✅ Opened check ${newCheck.check_number} for table ${table.table_number}`)
        return true
      }

      return false
    }
    catch (error) {
      log.error('Error opening check for table:', error)
      useErrorToast('Failed to open check')
      return false
    }
  }

  async function loadExistingCheck(checkId: number): Promise<boolean> {
    try {
      let check: Check | undefined = checksSyncStore.getCheckById(checkId)

      if (!check) {
        // Not in open-checks store — fetch directly (handles closed checks)
        const res = await get(`/api/business/${businessId.value}/checks/${checkId}`)
        check = res.data
      }

      if (!check) {
        useErrorToast('Check not found')
        return false
      }

      activeCheckId.value = check.id
      // Ensure closed checks always have a UUID before editing (mirrors desktop behaviour)
      if (check.status === 'closed' && !check.uuid) {
        check.uuid = crypto.randomUUID()
      }
      checkData.value = check
      selectedTableId.value = check.table_id || null
      isEditingClosedCheck.value = check.status === 'closed'

      if (check.status === 'closed') {
        originalCheckSnapshot.value = JSON.parse(JSON.stringify(check))
      }

      loadCartFromCheckData()

      log.info(`✅ Loaded check ${check.check_number} (${check.status})`)
      return true
    }
    catch (error) {
      log.error('Error loading check:', error)
      useErrorToast('Failed to load check')
      return false
    }
  }

  async function saveClosedCheckEdits(paymentData: {
    tax: number
    discount_amount: number
    tip: number
    payment_method_id: number | null
    payment_method_name: string | null
    discount_id: number | null
    discount_percentage: number | null
    tenders: import('~/types/checks').CheckTender[]
  }): Promise<boolean> {
    if (!isEditingClosedCheck.value || !checkData.value || !businessId.value) return false

    const check = checkData.value
    const newCart = checkCart.value
    const checkUuid = check.uuid

    // Mismo filtro que checkCartTotal, que es el monto que se le cobra al cliente.
    // Cuando aquí sólo se excluía 'canceled', un artículo anulado después de ir a
    // cocina seguía sumando: los tenders quedaban en $90 y checks.total en $120,
    // y /app/financial reportaba los $120 que nadie pagó.
    const newSubtotal = newCart
      .filter(item => item.kitchen_status !== 'canceled' && item.kitchen_status !== 'voided')
      .reduce((sum, item) => sum + (item.total || 0), 0)

    const newTotal = newSubtotal + paymentData.tax - paymentData.discount_amount + paymentData.tip

    const payload: SyncCheckPayload = {
      check_number: check.check_number,
      check_type: check.check_type,
      shift_id: check.shift_id ?? 0,
      customer_name: check.customer_name ?? null,
      customer_phone: check.customer_phone ?? null,
      guest_count: check.guest_count || 1,
      notes: check.notes ?? null,
      cart: newCart,
      subtotal: newSubtotal,
      tax: paymentData.tax,
      discount: paymentData.discount_amount,
      tip: paymentData.tip,
      total: newTotal,
      payment_method_id: paymentData.payment_method_id,
      payment_method_name: paymentData.payment_method_name,
      discount_id: paymentData.discount_id,
      discount_percentage: paymentData.discount_percentage,
      uuid: checkUuid ?? null,
      opened_at: check.opened_at,
      closed_at: check.closed_at ?? null,
      tenders: paymentData.tenders,
    }

    try {
      const res = await post(`/api/business/${businessId.value}/checks/sync`, payload)

      if (res.success) {
        if (checkUuid) {
          try {
            await post(`/api/business/${businessId.value}/checks/${checkUuid}/revise`, {
              trigger: 'admin_edit',
              original_check: originalCheckSnapshot.value,
            })
          }
          catch (revErr) {
            log.warn('Revision recording failed (non-fatal):', revErr)
          }
        }

        useSuccessToast('Check updated successfully')
        await navigateTo('/app/checks')
        cancelClosedCheckEdit()
        return true
      }
      useErrorToast('Failed to save check edits')
    }
    catch (error: any) {
      log.error('Error saving closed check edits:', error)
      useErrorToast(error?.data?.message || 'Failed to save check edits')
    }
    return false
  }

  function cancelClosedCheckEdit(): void {
    isEditingClosedCheck.value = false
    activeCheckId.value = null
    checkData.value = null
    selectedTableId.value = null
    checkCart.value = []
    originalCheckSnapshot.value = null
  }

  async function openTablelessCheck(params: {
    check_type: 'togo' | 'delivery' | 'custom'
    customer_id?: number
    customer_name?: string
    customer_phone?: string
    custom_label?: string
  }): Promise<boolean> {
    try {
      if (shiftStatus.value === 'closed') {
        useErrorToast('Please open a shift to create an order')
        return false
      }

      // Prevent same customer from having two open checks
      if (params.customer_id) {
        await checksSyncStore.fetchChecks()
        const existing = syncedChecks.value.find(
          (check: Check) => check.customer_id === params.customer_id && check.status === 'open',
        )
        if (existing) {
          const msg = t('{name} already has an open check', { name: params.customer_name || '' })
          useErrorToast(msg)
          return false
        }
      }

      // Save current check if switching
      if (hasActiveCheck.value && hasItems.value) {
        await saveCheckToDatabase()
      }

      const checkForm = {
        check_type: params.check_type,
        shift_id: shiftId.value,
        guest_count: 1,
        customer_id: params.customer_id,
        customer_name: params.customer_name,
        customer_phone: params.customer_phone,
        custom_label: params.custom_label,
      }

      const success = await checksStore.createCheck(checkForm)
      if (!success) {
        return false
      }

      // Fetch and find the newly created check
      await checksSyncStore.fetchChecks()
      const matchingChecks = syncedChecks.value.filter(
        (check: Check) => !check.table_id
          && check.status === 'open'
          && check.check_type === params.check_type
          && (check.customer_name || null) === (params.customer_name || null)
          && (check.custom_label || null) === (params.custom_label || null),
      )
      const newCheck = matchingChecks[0]

      if (newCheck) {
        clearActiveCheck()
        activeCheckId.value = newCheck.id
        checkData.value = newCheck
        selectedTableId.value = null
        log.info(`✅ Opened tableless ${params.check_type} check ${newCheck.check_number}`)
      }
      else {
        log.warn('Could not find newly created check in synced store, but check was created')
      }

      return true
    }
    catch (error) {
      log.error('Error opening tableless check:', error)
      useErrorToast('Failed to open check')
      return false
    }
  }

  // Computed: open tableless checks
  const openTablelessChecks = computed(() => {
    return syncedChecks.value.filter(
      (check: Check) => !check.table_id && check.status === 'open',
    )
  })

  async function closeCheck(): Promise<boolean> {
    // Check if shift is open
    if (shiftStatus.value === 'closed') {
      useErrorToast('Please open a shift to create an order')
      return false
    }

    if (!activeCheckId.value) {
      return false
    }

    try {
      // Update table status back to available (only for table-based checks)
      if (selectedTableId.value) {
        await tablesStore.updateTableStatus(selectedTableId.value, 'available')
      }

      // Close the check (updateStatus automatically sets closed_at in repository)
      const checkId = activeCheckId.value
      await checksStore.updateStatus(checkId, 'closed')

      // Remove from synced checks and re-fetch to ensure clean state
      checksSyncStore.removeCheckFromArray(checkId)
      await checksSyncStore.fetchChecks()

      // Clear active state
      clearActiveCheck()

      // Toast removed - realtime sync will show feedback when check closes
      return true
    }
    catch (error) {
      log.error('Error closing check:', error)
      useErrorToast('Failed to close check')
      return false
    }
  }

  function clearActiveCheck(): void {
    activeCheckId.value = null
    checkData.value = null
    selectedTableId.value = null
    checkCart.value = []
    log.info('✅ Cleared active check and cart')
  }

  function parkCheck(): void {
    // Park check - clears context but check remains open in database
    clearActiveCheck()
    log.info('✅ Check parked (cleared from context)')
  }

  // Smart table selection - load existing check or create new one
  async function selectTableWithConfirm(tableId: number): Promise<void> {
    // Check if shift is open
    if (shiftStatus.value === 'closed') {
      useErrorToast('Please open a shift to create an order')
      return
    }

    const table = tablesStore.getTableById(tableId)
    if (!table) {
      useErrorToast('Table not found')
      return
    }

    // Save current check if switching from another table
    if (hasActiveCheck.value && selectedTableId.value !== tableId && hasItems.value) {
      log.info('💾 Saving current check before switching tables')
      await saveCheckToDatabase()
    }

    // Check if there's already an open check for this table
    await checksSyncStore.fetchChecks()

    // Debug: Log all checks
    log.debug('All checks:', syncedChecks.value)
    log.debug('Looking for table_id:', tableId)

    const existingCheck = syncedChecks.value.find(
      (check: Check) => {
        log.debug(`Comparing: check.table_id=${check.table_id} (${typeof check.table_id}) with tableId=${tableId} (${typeof tableId}), status=${check.status}`)
        return check.table_id === tableId && check.status === 'open'
      },
    )

    log.debug('Existing check found:', existingCheck)

    if (existingCheck) {
      // Clear current check state before loading new one
      clearActiveCheck()
      // Load existing check into context silently
      await loadExistingCheck(existingCheck.id)
    }
    else {
      // No check exists - open dialog for optional client selection
      $dialog.open(NewDineInCheckDialog, {
        data: { tableNumber: table.table_number, openChecks: syncedChecks.value },
        props: {
          header: t('Open new check for table {tableNumber}?', { tableNumber: table.table_number }),
          modal: true,
          dismissableMask: true,
          style: { width: '24rem' },
        },
        onClose: async (options: any) => {
          const result = options?.data
          if (result?.confirmed) {
            await openCheckForTable(tableId, result.customerData)
          }
        },
      })
    }
  }

  // Cart manipulation methods
  function addItemToCheckCart(item: CartItem): void {
    const checkCartItem: CheckCartItem = {
      ...item,
      kitchen_status: 'not_sent',
      kitchen_history: [],
    }
    checkCart.value.push(checkCartItem)
    syncCartToCheckData()
    log.info('✅ Item added to check cart')
  }

  async function removeItemFromCheckCart(cartUuid: string): Promise<void> {
    const item = checkCart.value.find(i => i.cart_uuid === cartUuid)
    if (!item)
      return

    // Check if item was already sent to kitchen
    if (
      item.kitchen_status === 'sent_to_kitchen'
      || item.kitchen_status === 'ready'
      || item.kitchen_status === 'served'
    ) {
      // Items already sent must use void dialog (has cost)
      useWarnToast('Item already sent to kitchen. Use void instead.')
      // TODO: Open void dialog instead
      return
    }

    // Store original status in case we need to revert
    const originalStatus = item.kitchen_status

    // Mark as canceled instead of deleting
    item.kitchen_status = 'canceled'
    item.cancel_info = {
      reason: 'customer_changed_mind',
      canceled_at: new Date().toISOString(),
      canceled_by: undefined, // TODO: Get from user session
    }
    item.kitchen_history.push({
      action: 'canceled',
      timestamp: new Date().toISOString(),
      user_id: undefined, // TODO: Get from user session
    })

    // Sync to local check data
    syncCartToCheckData()

    // Save to database to persist cancellation and trigger KDS update
    const success = await saveCheckToDatabase()

    // If save failed, revert the cancellation
    if (!success) {
      item.kitchen_status = originalStatus
      delete item.cancel_info
      item.kitchen_history.pop()
      syncCartToCheckData()
      log.warn('⚠️ Item cancellation reverted due to save failure')
      return
    }

    log.info('✅ Item canceled and saved to database')
  }

  async function voidItemFromCheckCart(cartUuid: string, reason: string, notes: string): Promise<void> {
    const item = checkCart.value.find(i => i.cart_uuid === cartUuid)
    if (!item)
      return

    // Can only void items that have been sent to kitchen
    if (
      item.kitchen_status !== 'sent_to_kitchen'
      && item.kitchen_status !== 'ready'
      && item.kitchen_status !== 'served'
    ) {
      useWarnToast('Can only void items that have been sent to kitchen')
      return
    }

    const originalStatus = item.kitchen_status

    // Mark as voided
    item.kitchen_status = 'voided'
    item.void_info = {
      reason: reason as any,
      notes,
      voided_at: new Date().toISOString(),
      voided_by: '', // TODO: Add user ID from session
    }
    item.kitchen_history.push({
      action: 'voided',
      timestamp: new Date().toISOString(),
      user_id: undefined,
    })

    syncCartToCheckData()
    const success = await saveCheckToDatabase()

    // Revert if save failed
    if (!success) {
      item.kitchen_status = originalStatus
      delete item.void_info
      item.kitchen_history.pop()
      syncCartToCheckData()
      log.warn('⚠️ Item void reverted due to save failure')
      return
    }

    log.info('✅ Item voided and saved to database')
  }

  function increaseItemQuantity(item: CheckCartItem): void {
    // Track modification if item was already sent
    if (item.kitchen_status === 'sent_to_kitchen') {
      item.kitchen_history.push({
        action: 'modified',
        timestamp: new Date().toISOString(),
      })
    }
    item.quantity += 1
    item.total = calculateCheckCartItemTotal(item)
    syncCartToCheckData()
  }

  async function decreaseItemQuantity(item: CheckCartItem): Promise<void> {
    if (item.quantity === 1) {
      await removeItemFromCheckCart(item.cart_uuid)
      return
    }
    // Track modification if item was already sent
    if (item.kitchen_status === 'sent_to_kitchen') {
      item.kitchen_history.push({
        action: 'modified',
        timestamp: new Date().toISOString(),
      })
    }
    item.quantity -= 1
    item.total = calculateCheckCartItemTotal(item)
    syncCartToCheckData()
  }

  function clearCheckCart(): void {
    checkCart.value = []
    syncCartToCheckData()
    log.info('✅ Check cart cleared')
  }

  // Sync cart with checkData
  function syncCartToCheckData(): void {
    if (hasActiveCheck.value && checkData.value) {
      checkData.value.cart = checkCart.value
      // Calculate totals from cart
      const subtotal = Number.parseFloat(checkCartTotal.value)
      checkData.value.subtotal = subtotal
      checkData.value.total = subtotal // TODO: Add tax/discount calculation
      log.debug('Synced cart to check data')
    }
  }

  // Load cart from check data
  function loadCartFromCheckData(): void {
    if (checkData.value?.cart) {
      // Convert CartItems to CheckCartItems if needed
      // Preserve ALL kitchen status fields from database
      checkCart.value = checkData.value.cart.map((item: any) => ({
        ...item,
        kitchen_history: item.kitchen_history || [],
      }))
      log.info('✅ Loaded cart from check data')
    }
  }

  // Sync with commerce cart (when items are added from product selection)
  function syncFromCommerceCart(): void {
    if (hasActiveCheck.value && commerceStore.cart.length > 0) {
      // Track if new items were added
      let hasNewItems = false

      // Add new items from commerce cart to check cart
      commerceStore.cart.forEach((item) => {
        const existingItem = checkCart.value.find(ci => ci.cart_uuid === item.cart_uuid)
        if (!existingItem) {
          const checkCartItem: CheckCartItem = {
            ...item,
            kitchen_status: 'not_sent',
            kitchen_history: [],
          }
          checkCart.value.push(checkCartItem)
          hasNewItems = true
        }
      })

      // Clear commerce cart after syncing
      commerceStore.clearCart()
      syncCartToCheckData()

      // Auto-save to persist items immediately (but don't send to kitchen yet)
      if (hasNewItems) {
        // Use nextTick to ensure cart is fully synced
        nextTick(async () => {
          await saveCheckToDatabase()
        })
      }

      log.info('✅ Synced items from commerce cart to check cart', { hasNewItems })
    }
  }

  /**
   * Quick-add a no-modifier product directly to the check cart.
   * If the product already exists in the check cart, increments its quantity.
   * If not found, delegates to commerce store quickAddToCart (watcher handles sync).
   * Returns the cart_uuid of the added/updated item.
   */
  function quickAddToCheckCart(product: { id: number, name: string, price: number, modifiers?: unknown[] }): string {
    if (!hasActiveCheck.value) return ''

    const existingItem = checkCart.value.find(item =>
      item.items?.[0]?.product_id === product.id
      && (!item.modifiers || item.modifiers.length === 0)
      && item.kitchen_status === 'not_sent',
    )

    if (existingItem) {
      existingItem.quantity += 1
      existingItem.total = calculateCheckCartItemTotal(existingItem)
      syncCartToCheckData()

      nextTick(async () => {
        await saveCheckToDatabase()
      })

      return existingItem.cart_uuid
    }

    // New item — delegate to commerce store, watcher will sync
    return commerceStore.quickAddToCart(product as any)
  }

  // Check-specific actions
  async function saveCheckToDatabase(paymentData?: { tax: number, discount: number, tip: number }): Promise<boolean> {
    if (!activeCheckId.value || !businessId.value) {
      useErrorToast('No active check')
      return false
    }

    // Editando una cuenta CERRADA no se persiste nada hasta el guardado final.
    //
    // El servidor reconcilia inventario comparando el carrito que tiene guardado
    // contra el que llega en el sync. Cada guardado intermedio le movía esa línea
    // de salida: al anular un artículo, el carrito guardado ya traía el anulado,
    // el sync veía los dos lados iguales, y el stock nunca se reponía.
    //
    // Guardar sólo al final también hace que abandonar la edición no deje rastro
    // —ni en el carrito ni en el stock— en vez de dejar la cuenta a medio editar.
    //
    // Efecto conocido: mientras dura la edición el KDS no se entera de nada. En
    // una cuenta cerrada la comida ya se sirvió, así que no aplica.
    if (isEditingClosedCheck.value) {
      syncCartToCheckData()
      log.debug('Closed-check edit: cart kept in memory until final save')
      return true
    }

    try {
      const checkId = activeCheckId.value
      const subtotal = Number.parseFloat(checkCartTotal.value)
      const tax = paymentData?.tax || 0
      const discount = paymentData?.discount || 0
      const tip = paymentData?.tip || 0
      const total = subtotal + tax - discount + tip

      const totals = {
        subtotal,
        tax,
        discount,
        tip,
        total,
      }

      const response = await put(
        `/api/business/${businessId.value}/checks/${checkId}/cart`,
        {
          cart: checkCart.value,
          totals,
        },
      )

      if (response.success) {
        // Toast removed - saveCheckToDatabase is called internally by sendToKitchen and closeCheck
        // Those functions show their own specific success messages
        // useSuccessToast(t('Check saved successfully')
        // Update the check data with the response
        if (checkData.value && response.data) {
          checkData.value.cart = response.data.cart
          checkData.value.subtotal = response.data.subtotal
          checkData.value.total = response.data.total
        }
        return true
      }
      else {
        useErrorToast('Failed to save check')
        return false
      }
    }
    catch (error) {
      log.error('Error saving check:', error)
      useErrorToast('Failed to save check')
      return false
    }
  }

  async function sendToKitchen(): Promise<boolean> {
    if (!activeCheckId.value) {
      useErrorToast('No active check')
      return false
    }

    try {
      // Pre-send sync: Fetch latest kitchen statuses with retry
      let retries = 3
      let syncSuccessful = false

      while (retries > 0 && !syncSuccessful) {
        try {
          log.info('pre-send-sync', `Fetching latest kitchen statuses (${4 - retries}/3)`)
          await syncKitchenStatusesFromDatabase()
          syncSuccessful = true
        }
        catch {
          retries--
          if (retries === 0) {
            log.error('pre-send-sync:failed', 'Failed to sync kitchen statuses after 3 retries')
            useErrorToast('Failed to sync kitchen status. Please try again.')
            return false
          }
          log.warn('pre-send-sync:retry', `Retry attempt ${4 - retries}/3 in 1 second`)
          await new Promise(resolve => setTimeout(resolve, 1000))
        }
      }

      const now = new Date().toISOString()

      // Mark all unsent items as sent to kitchen
      // Re-sent items that were modified will get a new history entry
      checkCart.value.forEach((item) => {
        if (item.kitchen_status === 'not_sent') {
          item.kitchen_status = 'sent_to_kitchen'
          item.sent_to_kitchen_at = now
          item.kitchen_history.push({
            action: 'sent',
            timestamp: now,
          })
        }
        else if (item.kitchen_status === 'sent_to_kitchen') {
          // Check if item has modifications (last action is 'modified')
          const lastAction = item.kitchen_history[item.kitchen_history.length - 1]
          if (lastAction?.action === 'modified') {
            item.kitchen_history.push({
              action: 're-sent',
              timestamp: now,
            })
          }
        }
      })

      // TODO: Implement kitchen printing logic
      await saveCheckToDatabase()
      // Toast removed - reduces notification noise, action is self-evident
      // useSuccessToast(t('Order sent to kitchen')
      return true
    }
    catch (error) {
      log.error('Error sending to kitchen:', error)
      useErrorToast('Failed to send to kitchen')
      return false
    }
  }

  async function closeCheckWithPayment(paymentData: {
    tax: number
    discount_amount: number
    tip: number
    payment_method_id: number | null
    payment_method_name: string | null
    discount_id: number | null
    discount_percentage: number | null
    tenders: import('~/types/checks').CheckTender[]
  }): Promise<boolean> {
    // Check if shift is open
    if (shiftStatus.value === 'closed') {
      useErrorToast('Please open a shift to create an order')
      return false
    }

    if (!activeCheckId.value) {
      useErrorToast('No active check')
      return false
    }

    if (checkCart.value.length === 0) {
      useErrorToast('Cannot close an empty check')
      return false
    }

    try {
      const checkId = activeCheckId.value

      // Save check with payment details
      const saved = await saveCheckToDatabase({
        tax: paymentData.tax,
        discount: paymentData.discount_amount,
        tip: paymentData.tip,
      })
      if (!saved) {
        return false
      }

      // Update check with payment method and discount (both IDs and values for audit)
      if (businessId.value && checkId) {
        await put(
          `/api/business/${businessId.value}/checks/${checkId}`,
          {
            payment_method_id: paymentData.payment_method_id,
            payment_method_name: paymentData.payment_method_name,
            discount_id: paymentData.discount_id,
            discount_percentage: paymentData.discount_percentage,
          },
        )
      }

      // Close the check (updates status and table)
      const closed = await closeCheck()
      if (!closed) {
        return false
      }

      // Write payment tenders after close. A fully-discounted ($0) check needs
      // no tender: nothing was actually paid, so amount_applied is 0 — which the
      // DB rejects (check_tenders_amount_applied_check requires amount_applied > 0).
      // Drop zero-applied tenders so a 100%-discount close doesn't 500; the
      // payment method is already recorded on the check via the PUT above.
      const applicableTenders = (paymentData.tenders ?? []).filter(tender => tender.amount_applied > 0)
      if (applicableTenders.length && businessId.value && checkId) {
        await post(`/api/business/${businessId.value}/checks/${checkId}/tenders`, {
          tenders: applicableTenders,
        })
      }

      // Fetch complete check data for receipt
      if (businessId.value && checkId) {
        const response = await get(`/api/business/${businessId.value}/checks/${checkId}`)
        if (response.data) {
          openReceiptDialog(response.data)
        }
      }

      // Clear local cart
      clearCheckCart()

      return true
    }
    catch (error) {
      log.error('Error closing check:', error)
      useErrorToast('Failed to close check')
      return false
    }
  }

  function openReceiptDialog(checkData: Check): void {
    $dialog.open(POSCheckReceiptDialog, {
      data: { checkData },
      props: {
        header: t('Receipt'),
        ...useDynamicDialogSettings,
      },
    })
  }

  // Sync kitchen statuses from database (for realtime updates from KDS)
  async function syncKitchenStatusesFromDatabase(): Promise<void> {
    if (!activeCheckId.value || !businessId.value) {
      return
    }

    try {
      const response = await get(
        `/api/business/${businessId.value}/checks/${activeCheckId.value}`,
      )

      if (response.data?.cart) {
        // Merge kitchen statuses into existing cart without replacing other data
        checkCart.value = checkCart.value.map((localItem) => {
          // Skip not_sent and voided items - preserve waiter's work-in-progress
          if (localItem.kitchen_status === 'not_sent' || localItem.kitchen_status === 'voided') {
            return localItem
          }

          const dbItem = response.data.cart.find(
            (db: any) => db.cart_uuid === localItem.cart_uuid,
          )

          if (dbItem) {
            // Update ONLY kitchen-related fields, preserve everything else
            return {
              ...localItem,
              kitchen_status: dbItem.kitchen_status,
              kitchen_history: dbItem.kitchen_history || [],
              sent_to_kitchen_at: dbItem.sent_to_kitchen_at,
              marked_ready_at: dbItem.marked_ready_at,
              marked_served_at: dbItem.marked_served_at,
              void_info: dbItem.void_info,
            }
          }
          return localItem
        })

        log.info('✅ Synced kitchen statuses from database')
      }
    }
    catch (error) {
      log.error('Error syncing kitchen statuses:', error)
    }
  }

  function mergeKitchenStatusesFromPayload(incomingCart: any[]): void {
    if (!incomingCart?.length) return
    checkCart.value = checkCart.value.map((localItem) => {
      if (localItem.kitchen_status === 'not_sent' || localItem.kitchen_status === 'voided') {
        return localItem
      }
      const dbItem = incomingCart.find((db: any) => db.cart_uuid === localItem.cart_uuid)
      if (!dbItem) return localItem
      return {
        ...localItem,
        kitchen_status: dbItem.kitchen_status,
        kitchen_history: dbItem.kitchen_history || localItem.kitchen_history,
        sent_to_kitchen_at: dbItem.sent_to_kitchen_at,
        marked_ready_at: dbItem.marked_ready_at,
        marked_served_at: dbItem.marked_served_at,
        void_info: dbItem.void_info,
      }
    })
    log.info('✅ Merged kitchen statuses from realtime payload')
  }

  // Edit methods for check-specific cart
  function editProductInCheckCart(cartUuid: string): void {
    const item = checkCart.value.find(i => i.cart_uuid === cartUuid)
    if (!item || item.type !== 'PRODUCT') {
      log.error('Product not found in check cart')
      return
    }

    if (!item.items || item.items.length === 0) {
      log.error('Product item has no items array')
      return
    }

    const productItem = item.items[0]

    // Transform cart item structure to match fresh product structure for staging
    const productForStaging = {
      id: productItem.product_id,
      name: item.name,
      price: item.base_price,
      base_price: item.base_price,
      cart_uuid: item.cart_uuid,
      quantity: item.quantity,
      total: item.total,
      type: item.type || 'PRODUCT',
      custom: item.custom || [],
      modifiers: item.modifiers || productItem.modifiers || [],
      notes: item.notes,
      optionSelected: null,
    }

    productStaged.value = productForStaging
    productModal.value = true
  }

  function updateProductInCheckCart(product: any): void {
    if (!product.cart_uuid) {
      log.error('Product missing cart_uuid for update')
      return
    }

    const index = checkCart.value.findIndex(item => item.cart_uuid === product.cart_uuid)

    if (index !== -1) {
      const item = checkCart.value[index]

      // Track modification if item was already sent
      if (item.kitchen_status === 'sent_to_kitchen') {
        item.kitchen_history.push({
          action: 'modified',
          timestamp: new Date().toISOString(),
        })
      }

      const modifiers = product.modifiers
        ? JSON.parse(JSON.stringify(product.modifiers))
        : []

      const updatedProduct: CheckCartItem = {
        cart_uuid: product.cart_uuid,
        name: product.name,
        type: 'PRODUCT',
        base_price: product.price,
        quantity: product.quantity || 1,
        total: product.total || product.price,
        modifiers,
        notes: product.notes,
        items: [{
          id: 'product_item',
          product_id: product.id,
          product_name: product.name,
          quantity: product.quantity || 1,
          base_price: product.price,
          group: null,
        }],
        custom: product.custom,
        kitchen_status: item.kitchen_status,
        kitchen_history: item.kitchen_history,
        sent_to_kitchen_at: item.sent_to_kitchen_at,
      }

      updatedProduct.total = calculateCheckCartItemTotal(updatedProduct)

      checkCart.value[index] = updatedProduct
      syncCartToCheckData()
      productStaged.value = null
      useSuccessToast('Product updated successfully!')
    }
    else {
      useErrorToast('Product not found in cart')
    }
  }

  async function editComboInCheckCart(cartUuid: string): Promise<void> {
    const item = checkCart.value.find(i => i.cart_uuid === cartUuid)

    if (!item || (item.type !== 'COMBO' && item.type !== 'CUSTOM_COMBO')) {
      log.error('Combo not found in check cart')
      return
    }

    // Find the original combo from the commerce store
    let originalCombo = null
    if (item.combo_id) {
      originalCombo = combos.value.find(combo => String(combo.id) === String(item.combo_id))
    }

    if (!originalCombo && item.name) {
      originalCombo = combos.value.find(combo => combo.name === item.name)
    }

    if (originalCombo) {
      // Use shared editor to set up combo state
      comboEditor.openComboModal(originalCombo)
      comboEditor.comboStaged.quantity = item.quantity
      comboEditor.comboStaged.cart_uuid = cartUuid
      comboEditor.comboStaged.base_price = item.base_price

      // Enable customization if it's a custom combo
      if (item.type === 'CUSTOM_COMBO') {
        comboEditor.customize()

        // Restore saved selections from cart item
        if (item.items && Array.isArray(item.items) && comboEditor.comboCustomOptions && comboEditor.comboCustomOptions.items) {
          // Create a new array to trigger reactivity
          comboEditor.comboCustomOptions.items = (comboEditor.comboCustomOptions.items as any[]).map((group: any) => {
            // Find matching saved item for this group
            const savedItem = item.items?.find((saved: any) => saved.group?.id === group.group_id)

            if (savedItem) {
              // Restore the selected product and its modifiers
              return {
                ...group,
                selected: {
                  group_id: savedItem.group?.id || 0,
                  product_id: savedItem.product_id,
                  product_name: savedItem.product_name,
                  quantity: savedItem.quantity,
                  price_variation: savedItem.price_variation || 0,
                  selected_modifiers: savedItem.selected_modifiers || {},
                },
              }
            }

            return group
          })
        }

        comboModal.value = true
      }
      else {
        comboModal.value = true
      }
    }
    else {
      log.error('Original combo not found', {
        cartUuid,
        itemComboId: item.combo_id,
        itemName: item.name,
        availableCombos: combos.value.map(c => ({ id: c.id, name: c.name })),
      })
    }
  }

  function updateComboInCheckCart(combo: any): void {
    if (!combo.cart_uuid) {
      log.error('Combo missing cart_uuid for update')
      return
    }

    const index = checkCart.value.findIndex(item => item.cart_uuid === combo.cart_uuid)

    if (index !== -1) {
      const item = checkCart.value[index]

      // Track modification if item was already sent
      if (item.kitchen_status === 'sent_to_kitchen') {
        item.kitchen_history.push({
          action: 'modified',
          timestamp: new Date().toISOString(),
        })
      }

      const normalizedCombo: CheckCartItem = {
        cart_uuid: combo.cart_uuid,
        name: combo.name,
        type: 'COMBO',
        base_price: combo.base_price,
        quantity: combo.quantity || 1,
        total: 0,
        combo_id: String(combo.combo_id),
        items: combo.items
          ? combo.items.map((comboItem: any, idx: number) => ({
              id: `item_${idx}`,
              product_id: comboItem.product_id || 0,
              product_name: comboItem.product_name || '',
              quantity: comboItem.quantity || 1,
              base_price: 0,
              price_variation: comboItem.price_variation || 0,
              group: comboItem.group_id
                ? {
                    id: comboItem.group_id,
                    name: comboItem.group_name || '',
                    required: true,
                  }
                : null,
              selected_modifiers: comboItem.selected_modifiers || {},
            }))
          : [],
        kitchen_status: item.kitchen_status,
        kitchen_history: item.kitchen_history,
        sent_to_kitchen_at: item.sent_to_kitchen_at,
      }

      normalizedCombo.total = calculateCheckCartItemTotal(normalizedCombo)

      checkCart.value[index] = normalizedCombo
      syncCartToCheckData()
      comboEditor.reset()
      useSuccessToast('Combo updated successfully!')
    }
    else {
      useErrorToast('Combo not found in cart')
    }
  }

  function updateCustomComboInCheckCart(): void {
    if (!comboEditor.comboCustomOptions) {
      log.error('No custom combo options to update')
      return
    }

    const cartUuid = comboEditor.comboStaged.cart_uuid as string
    if (!cartUuid) {
      log.error('Combo missing cart_uuid for update')
      return
    }

    const index = checkCart.value.findIndex(item => item.cart_uuid === cartUuid)

    if (index !== -1) {
      const item = checkCart.value[index]

      const normalizedCombo: CheckCartItem = {
        cart_uuid: cartUuid,
        name: comboEditor.comboStaged.name as string,
        type: 'CUSTOM_COMBO',
        base_price: comboEditor.comboStaged.base_price as number,
        quantity: comboEditor.comboStaged.quantity as number || 1,
        total: comboEditor.processedComboOptions.total as number,
        combo_id: String(comboEditor.comboStaged.combo_id),
        items: (comboEditor.processedComboOptions.selected as any[]).map((selectedItem: any, idx: number) => ({
          id: `item_${idx}`,
          product_id: selectedItem.product_id || 0,
          product_name: selectedItem.product_name || '',
          quantity: selectedItem.quantity || 1,
          base_price: 0,
          price_variation: selectedItem.price_variation || 0,
          group: selectedItem.group_id
            ? {
                id: selectedItem.group_id,
                name: selectedItem.group_name || '',
                required: true,
              }
            : null,
          selected_modifiers: selectedItem.selected_modifiers || {},
        })),
        kitchen_status: item.kitchen_status,
        kitchen_history: item.kitchen_history,
        sent_to_kitchen_at: item.sent_to_kitchen_at,
      }

      // Track modification if item was already sent
      if (item.kitchen_status === 'sent_to_kitchen') {
        normalizedCombo.kitchen_history.push({
          action: 'modified',
          timestamp: new Date().toISOString(),
        })
      }

      checkCart.value[index] = normalizedCombo
      syncCartToCheckData()
      comboEditor.reset()
      useSuccessToast('Custom combo updated successfully!')
    }
    else {
      useErrorToast('Combo not found in cart')
    }
  }

  // Combo quantity methods
  function increaseComboQuantityStaged(): void {
    comboEditor.increaseQuantity()
  }

  function decreaseComboQuantityStaged(): void {
    comboEditor.decreaseQuantity()
  }

  // Combo customization wrapper (delegates to shared editor)
  function customizeCombo(): void {
    comboEditor.customize()
  }

  // Validation wrapper (delegates to shared editor)
  function validateRequiredModifiers(): boolean {
    return comboEditor.validateRequiredModifiers()
  }

  function reset(): void {
    clearActiveCheck()
    checkCart.value = []
    log.info('✅ POS check store reset')
  }

  // Watch for active check changes - load cart
  watch(() => activeCheckId.value, (newCheckId) => {
    if (newCheckId) {
      loadCartFromCheckData()
    }
    else {
      clearCheckCart()
    }
  })

  // Watch for commerce cart changes (auto-sync when check is active)
  watch(() => commerceStore.cart.length, (newLength, oldLength) => {
    if (hasActiveCheck.value && newLength > oldLength) {
      syncFromCommerceCart()
    }
  })

  return {
    // Commerce data (read-only access)
    products,
    categories,
    combos,

    // Check state
    selectedTableId,
    activeCheckId,
    checkData,
    hasActiveCheck,
    selectedTable,
    isEditingClosedCheck,

    // Cart state
    checkCart,
    productStaged,
    productModal,
    comboStaged,
    comboModal,
    comboCustomized,
    comboCustomOptions,

    // UI preferences
    compactViewEnabled,

    // Cart computed
    itemsInCart,
    checkCartTotal,
    hasItems,
    productStagedTotal,
    comboStagedTotal,

    // Payment calculations
    subtotal,
    selectedPaymentMethod,
    selectedDiscount,
    discountAmount,
    subtotalAfterDiscount,
    paymentSurcharge,
    finalTotal,
    canConfirmPayment,
    setSelectedPaymentMethod,
    setSelectedDiscount,
    setTipAmount,
    resetPaymentState,

    // Check methods
    openCheckForTable,
    openTablelessCheck,
    loadExistingCheck,
    saveClosedCheckEdits,
    cancelClosedCheckEdit,
    closeCheck,
    openTablelessChecks,
    clearActiveCheck,
    parkCheck,
    selectTableWithConfirm,

    // Cart methods
    addItemToCheckCart,
    removeItemFromCheckCart,
    voidItemFromCheckCart,
    increaseItemQuantity,
    decreaseItemQuantity,
    clearCheckCart,
    syncCartToCheckData,
    loadCartFromCheckData,
    syncFromCommerceCart,
    quickAddToCheckCart,
    saveCheckToDatabase,
    sendToKitchen,
    closeCheckWithPayment,
    syncKitchenStatusesFromDatabase,
    mergeKitchenStatusesFromPayload,

    // Product/Combo editing
    editProductInCheckCart,
    updateProductInCheckCart,
    editComboInCheckCart,
    updateComboInCheckCart,
    updateCustomComboInCheckCart,
    increaseComboQuantityStaged,
    decreaseComboQuantityStaged,
    customizeCombo,
    validateRequiredModifiers,

    // Reset
    reset,
  }
}, {
  persist: {
    key: 'posCheck',
    storage: localStorage,
    paths: ['selectedTableId', 'activeCheckId', 'checkCart', 'compactViewEnabled', 'isEditingClosedCheck', 'originalCheckSnapshot'],
  },
})
